import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import BaseView from '../../../js/base-view';

const OPENED_MENU_CLASS = 'is-opened';
const TOGGLE_SELECTOR = '[data-site-navigation-toggle]';
const NAV_BAR_SELECTOR = '.site-navigation__nav-bar';
const MENU_SELECTOR = '.site-navigation__menu';
const SECONDARY_MENU_SELECTOR = '.site-navigation__secondary-wrapper';
const SECONDARY_MENU_NAV_SELECTOR = '.site-navigation__secondary-wrapper nav';
const EDITOR_INFO_SELECTOR = '.site-navigation__editor-info';

const TOP_MENU_SELECTOR = '.site-navigation__nav';

const MIN_FONT_SIZE_MOBILE = 24;
const MAX_FONT_SIZE_MOBILE = 28;
const MIN_FONT_SIZE_DESKTOP = 32;
const MAX_FONT_SIZE_DESKTOP = 40;

const TABLET_BREAKPOINT = 768;
const CUSTOM_DESKTOP_BREAKPOINT = 1440;

export default class SiteNavigation extends BaseView {
	/**
	 * Entrypoint
	 */
	init() {
		const isMobile = window.viewport_service.current_width < CUSTOM_DESKTOP_BREAKPOINT;

		// refs
		this.refs = {
			toggles: Array.from(this.el.querySelectorAll(TOGGLE_SELECTOR)),
			navbar: this.el.querySelector(NAV_BAR_SELECTOR),
			menu: this.el.querySelector(MENU_SELECTOR),
			menuItems: Array.from(this.el.querySelectorAll(`${ MENU_SELECTOR } a`)),
			secondaryMenu: this.el.querySelector(SECONDARY_MENU_SELECTOR),
			secondaryMenuNav: this.el.querySelector(SECONDARY_MENU_NAV_SELECTOR),
			editorInfo: this.el.querySelector(EDITOR_INFO_SELECTOR),

			topMenu: this.el.querySelector(TOP_MENU_SELECTOR),
			topMenuItems: this.el.querySelectorAll(`${TOP_MENU_SELECTOR} > *`),
		};

		// props
		this.props = {
			secondaryMenuFontSizes: {
				min: isMobile ? MIN_FONT_SIZE_MOBILE : MIN_FONT_SIZE_DESKTOP,
				max: isMobile ? MAX_FONT_SIZE_MOBILE : MAX_FONT_SIZE_DESKTOP,
			},
		};

		// state
		this.state = {
			isNavigationOpen: false,
		};

		// attach events
		this.bindEvents();

		if (this.refs.secondaryMenu) {
			this.handleSecondaryMenuFontSize();
		}

		if(this.refs.topMenu)
			this.fitTopMenuWidth();
	}

	/**
	 * Bind component's events
	 */
	bindEvents() {
		this.on('resize', this.onResize.bind(this));
		this.on('click', this.onToggleButtonClick.bind(this));
		this.on('keydown', this.el, this.handleKeyUp.bind(this));
	}

	// --------------------------------
	// #region Event Handlers
	// --------------------------------

	handleKeyUp(ev) {
		if (this.state.isNavigationOpen && ev.keyCode === 27) {
			this.toggleMenu();
		}
	}

	toggleMenu() {
		this.el.classList.toggle(OPENED_MENU_CLASS, ! this.state.isNavigationOpen);
		this.state.isNavigationOpen = ! this.state.isNavigationOpen;
		this.handleAriaAttributtes();
		this.handleTabIndexes();
		this.handleBodyScrollLock();
	}

	onToggleButtonClick(ev) {
		const toggleButton = ev.target.closest(TOGGLE_SELECTOR);
		if (! toggleButton) {
			return;
		}

		ev.preventDefault();
		this.toggleMenu();
	}

	handleAriaAttributtes() {
		this.refs.toggles.forEach((toggle) => {
			toggle.setAttribute('aria-expanded', this.state.isNavigationOpen);
		});
	}

	handleTabIndexes() {
		for (let index = 0; index < this.refs.menuItems.length; index++) {
			this.refs.menuItems[ index ].setAttribute('tabindex', this.state.isNavigationOpen ? 0 : -1);
		}
	}

	handleBodyScrollLock() {
		if (this.state.isNavigationOpen) {
			disableBodyScroll(this.el);
		}
		else {
			enableBodyScroll(this.el);
		}
	}

	setupSecondaryMenuMinFontSize() {
		const isMobile = window.viewport_service.current_width < CUSTOM_DESKTOP_BREAKPOINT;

		this.props.secondaryMenuFontSizes = {
			min: isMobile ? MIN_FONT_SIZE_MOBILE : MIN_FONT_SIZE_DESKTOP,
			max: isMobile ? MAX_FONT_SIZE_MOBILE : MAX_FONT_SIZE_DESKTOP,
		};
	}

	resetSecondaryMenuFontSize() {
		const isMobile = window.viewport_service.current_width < CUSTOM_DESKTOP_BREAKPOINT;
		this.refs.secondaryMenu.style.fontSize = isMobile ? `${ MAX_FONT_SIZE_MOBILE }px` : `${ MAX_FONT_SIZE_DESKTOP }px`;
	}

	menuFitsViewport() {
		const {
			navbar, secondaryMenu, secondaryMenuNav, editorInfo,
		} = this.refs;
		const isMobile = window.viewport_service.current_width < TABLET_BREAKPOINT;
		const targetRect = isMobile ? secondaryMenu.getBoundingClientRect() :
			secondaryMenuNav.getBoundingClientRect();
		const valueToCheck = isMobile ? targetRect.height : targetRect.bottom;
		let aditionalSums = 0;

		if (isMobile) {
			aditionalSums += navbar.getBoundingClientRect().height;
		}
		else {
			aditionalSums += editorInfo.getBoundingClientRect().height;
		}

		return valueToCheck <= (window.innerHeight - aditionalSums);
	}

	handleSecondaryMenuFontSize() {
		const { secondaryMenu } = this.refs;
		const { secondaryMenuFontSizes } = this.props;

		/**
		 *  💀 Let's play safe to not get stuck in the loop and crash the browser
		 * 10 covers the amount the font size needs to shrink or grow
		 */
		const maxIterations = 10;
		let iterationCount = 0;
		let currentFontSize = parseInt(window.getComputedStyle(secondaryMenu).fontSize, 10);

		while (currentFontSize > secondaryMenuFontSizes.min &&
				iterationCount < maxIterations &&
				! this.menuFitsViewport()
		) {
			currentFontSize -= 1;
			secondaryMenu.style.fontSize = `${ currentFontSize }px`;
			iterationCount += 1;
		}
	}

	fitTopMenuWidth() {
		const menuWidth = this.refs.topMenu.offsetWidth;
		let menuItemsWidth = [...this.refs.topMenuItems].reduce((totalWidth, item) => {
			return totalWidth + item.offsetWidth;
		}, 0);

		if(menuWidth > 0) {
			for (let i = this.refs.topMenuItems.length - 2; i >= 0; i--) { // loop between items except last one
				const element = this.refs.topMenuItems[i];

				if((menuItemsWidth + 10) > menuWidth) {
					menuItemsWidth -= element.offsetWidth;
					element.style.display = 'none';
				}
			}
		}
	}

	// /**
	//  * When the viewport size changes
	//  */
	onResize() {
		if (this.refs.secondaryMenu) {
			this.setupSecondaryMenuMinFontSize();
			this.resetSecondaryMenuFontSize();
			this.handleSecondaryMenuFontSize();
		}

		if(this.refs.topMenu)
			this.fitTopMenuWidth();
	}

	// --------------------------------
	// #endregion
	// --------------------------------

	/**
	 * Before the component gets destroyed
	 * - unbind any event not bound with this.on()
	 * - reset UI if needed (any classes/attributes added?)
	 */
	beforeDestroy() {}
}
